/* eslint-disable camelcase */
import { useAccountStore } from '@/store/modules/account/store.js';
import { useUserStore } from '@/store/modules/user/store.js';
import { get, isArray, uniqWith } from 'lodash-es';
import i18n from '@/plugins/vue-i18n.js';

import { findContextAccount } from '@/pinia/storeHelper.js';
import CreatedMethods from '@/entities/CreatedMethod.js';
import SupplierId from '@/entities/enums/SupplierId.js';
import Account from './Account.js';
import CommunicationMethod from './CommunicationMethod.js';
import CommunicationStatus from './CommunicationStatus.js';
import CommunicationType from './CommunicationType.js';
import Customer from './Customer.js';
import Dashboard from './Dashboard.js';
import Division from './Division.js';
import LeadEmail from './LeadEmail.js';
import LeadEmailType from './LeadEmailType.js';
import LeadPhone from './LeadPhone.js';
import LeadPhoneType from './LeadPhoneType.js';
import LeadResult from './LeadResult.js';
import LeadStatus from './LeadStatus.js';
import LeadType from './LeadType.js';
import LeadVehicle from './LeadVehicle.ts';
import LeadVehicleType from './LeadVehicleType.js';
import Model from './Model.js';
import ProgressState from './ProgressState.js';
import SaleState from './SaleState.js';
import TaskEventType from './TaskEventType.js';
import User from './User.js';

import ActivixDate from '../value-objects/ActivixDate.js';
import ClientCardSection from './ClientCardSection.js';

export default class Lead extends Model {
    static get defaults() {
        return {
            // IDs
            bdc_user_id: null,
            commercial_id: null,
            division_id: null,
            lead_type_id: null,
            second_bdc_user_id: null,
            second_user_id: null,
            secondary_user_id: null,
            service_advisor_id: null,
            service_agent_id: null,
            user_id: null,
            created_by: null,
            updated_by: null,

            // Fields
            dealer_tour: false,
            gas: false,
            unsubscribe_sms_date: null,
            unsubscribe_all_date: null,

            // Relationships (BelongsTo)
            customer: {},
            lead_type: {},

            // Relationships (HasMany)
            automation_jobs: [],
            renewal_alert_notifications: [],
            custom_fields: [],
            communications: [],
            emails: [],
            lead_events: [],
            manual_campaigns: [],
            phones: [],
            products: [],
            task_events: [],
            vehicles: [],
            campaigns: [],
            suppliers: [],
        };
    }

    constructor(initialValues = {}) {
        super(initialValues);
    }

    init(values) {
        delete values.sold_or_first_wanted_vehicle;

        super.init(values);
    }

    /**
     * Getters
     */
    get account() {
        return (
            findContextAccount(this.account_id) ||
            useAccountStore().getAccountById(this.account_id) ||
            this._account ||
            new Account()
        );
    }

    get customer() {
        return this._customer;
    }

    get lead_phones() {
        return this.phones;
    }

    get lead_emails() {
        return this.emails;
    }

    get fullName() {
        if (this.business && this.business_name) {
            return this.business_name.trim();
        }

        return [this.first_name, this.last_name].join(' ').trim();
    }

    get shortName() {
        if (this.business && this.business_name) {
            return this.business_name.trim();
        }

        const initial = (this.first_name || '').charAt(0);

        if (initial) {
            return `${initial}. ${this.last_name || ''}`.trim();
        }

        return (this.last_name || '').trim();
    }

    get exchange_vehicles() {
        return this.vehicles.filter((vehicle) => vehicle?.type == LeadVehicleType.EXCHANGE);
    }

    get wanted_vehicles() {
        return this.vehicles.filter((vehicle) => vehicle?.type == LeadVehicleType.WANTED);
    }

    get sold_or_first_wanted_vehicle() {
        return this.getSoldOrFirstWantedVehicle();
    }

    get renewalVehicle() {
        return this.exchange_vehicles[0];
    }

    get serviceVehicle() {
        return (
            this.exchange_vehicles.find((vehicle) => vehicle.service) || this.exchange_vehicles[0]
        );
    }

    get financialDataFreezed() {
        return this.freeze_financial_data && this.account.commercial;
    }

    get cellPhones() {
        return this.phones
            .filter((phone) => {
                return phone.type === LeadPhoneType.CELL;
            })
            .sort((a, b) => b.preferred - a.preferred);
    }

    get homeEmails() {
        return this.emails
            .filter((email) => {
                return email.type === LeadEmailType.HOME;
            })
            .sort((a, b) => b.preferred - a.preferred);
    }

    get homePhones() {
        return this.phones
            .filter((phone) => {
                return phone.type === LeadPhoneType.HOME;
            })
            .sort((a, b) => b.preferred - a.preferred);
    }

    get workEmails() {
        return this.emails
            .filter((email) => {
                return email.type === LeadEmailType.WORK;
            })
            .sort((a, b) => b.preferred - a.preferred);
    }

    get workPhones() {
        return this.phones
            .filter((phone) => {
                return phone.type === LeadPhoneType.WORK;
            })
            .sort((a, b) => b.preferred - a.preferred);
    }

    get preferredPhone() {
        return this.homePhones[0] || this.workPhones[0] || this.cellPhones[0];
    }

    get preferredEmail() {
        return this.homeEmails[0] || this.workEmails[0];
    }

    get hasValidEmail() {
        return this.lead_emails.some((email) => email.valid);
    }

    get isDuplicate() {
        return this.status === LeadStatus.DUPLICATE;
    }

    get isInvalid() {
        return this.status === LeadStatus.INVALID;
    }

    get isLost() {
        return this.status === LeadStatus.LOST;
    }

    get statusText() {
        const translationKey =
            {
                [LeadStatus.INVALID]: 'leadStatus.invalid',
                [LeadStatus.DUPLICATE]: 'leadStatus.duplicate',
                [LeadStatus.LOST]: 'leadStatus.lost',
            }[this.status] || (this.sale_date ? 'leadStatus.sale' : 'leadStatus.active');

        return i18n.t(translationKey);
    }

    get statusIcon() {
        return LeadStatus.getIcon(this.status, this.sale_date);
    }

    get statusIconColor() {
        return (
            {
                [LeadStatus.INVALID]: 'link-orange',
                [LeadStatus.DUPLICATE]: 'link-grey',
                [LeadStatus.LOST]: 'link-red',
            }[this.status] || 'link-green'
        );
    }

    get typeIcon() {
        return LeadType.getIcon(this.lead_type_id);
    }

    get contextDate() {
        let date = null;

        if (this.isService()) {
            date = this.appointment_date;
        } else {
            switch (this.lead_type_id) {
                case LeadType.PHONE:
                    date = this.call_date;
                    break;
                case LeadType.WALK_IN:
                    date = this.presented_date;
                    break;
                case LeadType.RENEWAL:
                    date = this.end_contract_date;
                    break;
            }
        }

        return new ActivixDate(date || this.created_at);
    }

    /**
     * Setters
     */

    set account(account) {
        // This setter is used to set the account internal object from API responses
        // to get additional relations. Otherwise, it will get by the account store
        this._account = account ? new Account(account) : null;
    }

    set user(user) {
        // This setter is used to set the user internal object from API responses
        // to get additional relations. Otherwise, it will get by the user store
        this._user = user;
    }

    set secondaryUser(secondaryUser) {
        // This setter is used to set the secondaryUser internal object from API responses
        // to get additional relations. Otherwise, it will get by the user store
        this._secondaryUser = new User(secondaryUser);
    }

    set customer(customer) {
        this._customer = new Customer(customer);
    }

    set client(client) {
        this._customer = new Customer(client);
    }

    set lead_phones(newPhones) {
        this.phones = newPhones;
    }

    set lead_emails(newEmails) {
        this.emails = newEmails;
    }

    set wanted_vehicles(vehicles) {
        this.vehicles = this.vehicles
            .filter((vehicle) => {
                return vehicle.type != LeadVehicleType.WANTED;
            })
            .concat(vehicles);
    }

    set exchange_vehicles(vehicles) {
        this.vehicles = this.vehicles
            .filter((vehicle) => {
                return vehicle.type != LeadVehicleType.EXCHANGE;
            })
            .concat(vehicles);
    }

    /**
     * Specifications
     */

    isAssigned() {
        return !!this.userId || !!this.secondaryUserId || !!this.bdcUserId;
    }

    isNotAssigned() {
        return !this.isAssigned();
    }

    isAssignedTo(userIds, extended = false) {
        if (!isArray(userIds)) {
            userIds = [userIds];
        }

        if (!extended) {
            return (
                userIds.includes(this.userId) ||
                userIds.includes(this.secondaryUserId) ||
                userIds.includes(this.bdcUserId)
            );
        }

        return (
            userIds.includes(this.userId) ||
            userIds.includes(this.secondaryUserId) ||
            userIds.includes(this.bdcUserId) ||
            userIds.includes(this.commercialId) ||
            userIds.includes(this.serviceAdvisorId) ||
            userIds.includes(this.serviceAgentId)
        );
    }

    isNotAssignedTo(userIds, extended = false) {
        return !this.isAssignedTo(userIds, extended);
    }

    isOfType(types) {
        if (!isArray(types)) {
            types = [types];
        }

        return types.includes(this.lead_type_id);
    }

    isNotOfType(types) {
        return !this.isOfType(types);
    }

    get isTRS() {
        return this.created_method === CreatedMethods.AUTOSYNC;
    }

    get latestTrsTradeInVehicleId() {
        return this.exchange_vehicles
            .map((v) => ({
                ...v,
                trsSupplier: v.suppliers?.find(
                    (suppliable) => suppliable.id === SupplierId.AUTOSYNC
                ),
            }))
            .filter((v) => v.trsSupplier)
            .sort((a, b) =>
                b.trsSupplier.state.imported_on.localeCompare(a.trsSupplier.state.imported_on)
            )
            .at(0)?.id;
    }

    isNew() {
        return this.division_id === Division.NEW;
    }

    isUsed() {
        return this.division_id === Division.USED;
    }

    isService() {
        return this.division_id === Division.SERVICE;
    }

    isFinance() {
        return this.division_id === Division.FINANCE;
    }

    isSold() {
        return !!this.sale_date;
    }

    isDelivered() {
        return !!this.delivered_date;
    }

    wasPresentedBetween(startDate, endDate) {
        return new ActivixDate(this.presented_date).isBetween(startDate, endDate);
    }

    wasBeBackBetween(startDate, endDate) {
        return new ActivixDate(this.be_back_date).isBetween(startDate, endDate);
    }

    wasSoldBetween(startDate, endDate) {
        return new ActivixDate(this.sale_date, 'date').isBetween(startDate, endDate);
    }

    wasBoughtBack() {
        return this.isOfType(LeadType.RENEWAL) && !!this.refinanced_date;
    }

    getAccountName() {
        return (get(this.account, 'name') || '-').trim();
    }

    getMobilePhones() {
        return (this.phones || []).filter((p) => p.mobile && p.validated);
    }

    getSmsCandidatePhones() {
        return (this.phones || []).filter((phone) => {
            return phone.valid && (!phone.validated || phone.mobile);
        });
    }

    getUniquePhones() {
        return uniqWith(this.phones || [], (a, b) => {
            return !a.valid || (a.type === b.type && a.phone === b.phone);
        });
    }

    async getAssignedUserEmails() {
        const assignedUsers = [this.user, this.bdcUser, this.commercialUser];

        return assignedUsers.map((user) => user?.email).filter((email) => email);
    }

    getEmailsString() {
        return (this.emails || [])
            .map((e) => {
                return new LeadEmail(e).toString();
            })
            .join('<br>');
    }

    getPhonesString() {
        return (this.phones || [])
            .map((p) => {
                return new LeadPhone(p).toString();
            })
            .join('<br>');
    }

    getVehiclesField(field, type = null) {
        const vehicleRelation = LeadVehicle.validType(type) ? type : 'lead';

        return (this[`${vehicleRelation}_vehicles`] || []).map((v) => v[field]).join('<br>');
    }

    getSoldVehicle() {
        return this.wanted_vehicles.find((v) => v.sold);
    }

    getPrimaryWantedVehicle() {
        return this.wanted_vehicles.find((v) => v.primary_vehicle);
    }

    getSoldOrFirstWantedVehicle() {
        return this.getSoldVehicle() || this.getPrimaryWantedVehicle() || this.wanted_vehicles[0];
    }

    hasVerifiedSoldVehicle() {
        return this.wanted_vehicles.some((v) => v.sold && v.verified_by_id);
    }

    isSoldOrRefinanced() {
        return !!(this.sale_date || this.sale_by_phone || this.refinanced_date);
    }

    isRefinanced() {
        return this.isOfType(LeadType.RENEWAL) && !!this.refinanced_date;
    }

    isRenewed() {
        return this.isOfType(LeadType.RENEWAL) && !!this.sale_date;
    }

    isValidForAutoRenew() {
        const soldVehicle = this.getSoldVehicle();

        if (!soldVehicle) {
            return false;
        }

        return !!soldVehicle.end_contract_date || soldVehicle.modality == 'cash';
    }

    canAutoRenew() {
        return (
            this.account.auto_renewal &&
            (![1, 2].includes(this.division_id) ||
                (this.isNew() && this.account.auto_renewal_new) ||
                (this.isUsed() && this.account.auto_renewal_used))
        );
    }

    getSaleState(start, end, carryOverOption) {
        start = ActivixDate.parseDate(start, 'start');
        end = ActivixDate.parseDate(end, 'end');

        const isDiscounted =
            this.account.client_card_fields.process.discounted && this.discounted_date;

        if (this.isForcedWaitingSale()) {
            return SaleState.WAITING_SALE;
        }

        if (this.status == 'lost') {
            return SaleState.LOST;
        }

        if (this.progress_state == ProgressState.REFUSED) {
            return SaleState.REFUSED;
        }

        const deliveryDate = new ActivixDate(this.delivery_date);
        const deliveredDate = new ActivixDate(this.delivered_date);

        if (carryOverOption && this.isCarryOver(start, end)) {
            if (this.delivered_date) {
                if (isDiscounted) {
                    return SaleState.DISCOUNTED;
                }

                return SaleState.CARRY_OVER;
            }

            if (this.delivery_date || this.deliverable_date) {
                return SaleState.DELIVERY_CARRY_OVER;
            }

            return SaleState.CARRY_OVER_NOT_DELIVERED;
        }

        if (this.isWaitingSale(start, end)) {
            return SaleState.WAITING_SALE;
        }

        if ((!this.delivered_date && deliveryDate.isAfter(end)) || deliveredDate.isAfter(end)) {
            return SaleState.POSTPONED;
        }

        if (!this.delivered_date && deliveryDate.isBetween(start, end)) {
            return SaleState.PLANNED;
        }

        if (!this.delivery_date) {
            if (this.approved) {
                return SaleState.APPROVED;
            }

            return SaleState.SOLD;
        }

        if (this.delivered_date) {
            if (isDiscounted) {
                return SaleState.DISCOUNTED;
            }

            return SaleState.DELIVERED;
        }

        return SaleState.NONE;
    }

    isCarryOver(start, end) {
        start = ActivixDate.parseDate(start, 'start');
        end = ActivixDate.parseDate(end, 'end');

        if (
            !this.sale_date ||
            this.status == 'lost' ||
            this.progress_state == ProgressState.REFUSED
        ) {
            return false;
        }

        const carryOverRange = Dashboard.getCarryOverRange(start, end);
        const saleDate = new ActivixDate(this.sale_date, 'date');
        const deliveryDate = new ActivixDate(this.delivery_date);
        const deliveredDate = new ActivixDate(this.delivered_date);
        const deliverableDate = new ActivixDate(this.deliverable_date);

        return (
            saleDate.isBefore(start) &&
            (deliveryDate.isBetween(start, end) ||
                deliveredDate.isBetween(start, end) ||
                deliverableDate.isBetween(start, end) ||
                (!this.delivery_date &&
                    !this.deliverable_date &&
                    saleDate.isBetween(carryOverRange.start, carryOverRange.end)))
        );
    }

    isUnsubscribedToAll() {
        return !!this.unsubscribe_all_date && new ActivixDate(this.unsubscribe_all_date).isFuture();
    }

    isUnsubscribedToEmail() {
        return this.isUnsubscribedToAll() || !!this.unsubscribe_email_date;
    }

    isUnsubscribedToCall() {
        return this.isUnsubscribedToAll() || !!this.unsubscribe_call_date;
    }

    isUnsubscribedToSms() {
        return this.isUnsubscribedToAll() || !!this.unsubscribe_sms_date;
    }

    isCaslExpired() {
        return !!this.customer.id && this.customer.isExpiredConsent;
    }

    isWaitingSale(start, end) {
        start = ActivixDate.parseDate(start, 'start');
        end = ActivixDate.parseDate(end, 'end');

        if (this.isForcedWaitingSale()) {
            return true;
        }

        const deliveryDate = new ActivixDate(this.delivery_date, 'date');
        const deliverableDate = new ActivixDate(this.deliverable_date, 'date');

        if (
            !this.account.enabledWaitingSale() ||
            this.status == 'lost' ||
            this.progress_state == ProgressState.REFUSED ||
            !this.sale_date ||
            this.delivered_date ||
            deliveryDate.isBetween(start, end) ||
            deliverableDate.isBetween(start, end)
        ) {
            return false;
        }

        const waitingSaleDate = new ActivixDate(this.account.waiting_sale_date, 'date');
        const saleDate = new ActivixDate(this.sale_date, 'date');

        return saleDate.isBefore(start) && saleDate.isSameOrAfter(waitingSaleDate);
    }

    isForcedWaitingSale() {
        return this.account.enabledWaitingSale() && this.waiting_sale && !this.delivered_date;
    }

    isCreatedBeforeServiceReachedRelease() {
        const serviceReleaseDate = new ActivixDate('2021-08-17 00:00');
        const leadCreationDate = new ActivixDate(this.created_at);

        return leadCreationDate.isBefore(serviceReleaseDate);
    }

    getLatestConnectedCommunication() {
        const connectedComms = this.communications.filter(
            (comm) => comm.connected && !comm.suppliers?.length
        );

        return connectedComms.reduce((latest, comm) => {
            const date = new ActivixDate(comm.original_created_at || comm.created_at);
            const latestDate = latest
                ? new ActivixDate(latest.original_created_at || latest.created_at)
                : null;
            return !latestDate || date.isAfter(latestDate) ? comm : latest;
        }, null);
    }

    getResult() {
        if (this.account?.isPolestar()) {
            const nonPrimaryEvents = this.lead_events?.filter(
                (event) => !event.is_primary_lead_event
            );
            const latestLeadEvent = nonPrimaryEvents?.length
                ? nonPrimaryEvents.reduce(
                      (latest, event) =>
                          !latest ||
                          new ActivixDate(event.original_created_at).isAfter(
                              new ActivixDate(latest.original_created_at)
                          )
                              ? event
                              : latest,
                      null
                  )
                : null;

            if (latestLeadEvent) {
                const lastComm = this.getLatestConnectedCommunication();

                if (
                    !lastComm ||
                    new ActivixDate(latestLeadEvent.original_created_at).isAfter(
                        new ActivixDate(lastComm.original_created_at || lastComm.created_at)
                    )
                ) {
                    return LeadResult.NEEDS_ATTENTION;
                }
            }
        }

        if (
            (this.isCreatedBeforeServiceReachedRelease() &&
                this.division_id == Division.SERVICE &&
                (this.appointment_date || this.appt_call_date)) ||
            this.presented_date ||
            this.presented_other_date ||
            (this.sale_date && this.account.mark_sold_lead_as_reached) ||
            this.delivered_date
        ) {
            return LeadResult.REACHED;
        }

        if (this.communications.length) {
            let reached = false;
            let attempted = false;

            for (const communication of this.communications) {
                // If comunication more than 60 seconds
                if (
                    !!communication.url &&
                    communication.duration_call >= 60 &&
                    (!this.result || this.result == LeadResult.REACHED)
                ) {
                    return LeadResult.REACHED;
                }

                // If incoming communication
                if (
                    communication.communication_type_id == CommunicationType.INCOMING &&
                    (communication.communication_method_id == CommunicationMethod.EMAIL ||
                        communication.created_by_user ||
                        communication?.created_by_api)
                ) {
                    return LeadResult.REACHED;
                }

                // If created or updated by user and set to answered
                if (
                    communication.status == CommunicationStatus.ANSWERED &&
                    (communication.created_by_user ||
                        communication.updated_by_user ||
                        communication?.created_by_api)
                ) {
                    return LeadResult.REACHED;
                }

                // If SMS communication
                if (
                    communication.communication_method_id == CommunicationMethod.SMS &&
                    communication.status == CommunicationStatus.ANSWERED
                ) {
                    return LeadResult.REACHED;
                }

                // If Messenger communication
                if (
                    communication.communication_method_id == CommunicationMethod.MESSENGER &&
                    (communication.exchange_counter > 0 || communication.outbound_counter > 0) &&
                    communication.status != CommunicationStatus.ATTEMPTED
                ) {
                    return LeadResult.REACHED;
                }

                if (
                    communication.communication_method_id == CommunicationMethod.VIDEO &&
                    [CommunicationStatus.CALLING, CommunicationStatus.ANSWERED].includes(
                        communication.status
                    )
                ) {
                    return LeadResult.REACHED;
                }

                // If answered communication or lead is reached or communication has appointment (deprecated)
                if (
                    communication.status == CommunicationStatus.ANSWERED ||
                    this.result == LeadResult.REACHED ||
                    (this.communications[0] && this.communications[0].appointment)
                ) {
                    reached = true;
                }

                if (
                    this.result == LeadResult.ATTEMPTED ||
                    (communication.affect_lead_status &&
                        communication.status == CommunicationStatus.ATTEMPTED)
                ) {
                    attempted = true;
                }
            }

            if (this.isOfType(LeadType.WALK_IN) && this.created_by_user) {
                return LeadResult.REACHED;
            }

            if (reached && (this.result != LeadResult.ATTEMPTED || this.isResultSet())) {
                return LeadResult.REACHED;
            }

            if (attempted) {
                return LeadResult.ATTEMPTED;
            }

            return LeadResult.PENDING;
        }

        if (this.result == LeadResult.REACHED) {
            return LeadResult.REACHED;
        }

        if (this.result == LeadResult.ATTEMPTED) {
            return LeadResult.ATTEMPTED;
        }

        if (this.result === LeadResult.NEEDS_ATTENTION) {
            return LeadResult.NEEDS_ATTENTION;
        }

        return LeadResult.PENDING;
    }

    isResultSet() {
        if (this.id == 0) {
            return false;
        }

        if (
            (this.isCreatedBeforeServiceReachedRelease() &&
                this.division_id == Division.SERVICE &&
                (this.appointment_date || this.appt_call_date)) ||
            this.presented_date ||
            this.presented_other_date ||
            this.sale_date ||
            this.delivered_date ||
            this.road_test_date ||
            this.home_road_test_date ||
            this.be_back_date ||
            this.take_over_date
        ) {
            return true;
        }

        for (const communication of this.communications || []) {
            if (communication.communication_type_id == CommunicationType.INCOMING) {
                return true;
            }

            if (
                communication.communication_type_id == CommunicationType.OUTGOING &&
                communication.communication_method_id == CommunicationMethod.PHONE &&
                communication.status == CommunicationStatus.ANSWERED &&
                !communication.created_by_user &&
                !communication?.created_by_api
            ) {
                return true;
            }

            if (
                communication.communication_method_id == CommunicationMethod.SMS &&
                communication.status == CommunicationStatus.ANSWERED
            ) {
                return true;
            }
        }

        return false;
    }

    hasDeliveryMan(userId) {
        if (!userId) {
            return false;
        }

        return this.task_events.some((task) => {
            return (
                task.task_event_type_id == TaskEventType.DELIVERY && this.deliveredById == userId
            );
        });
    }

    hasInvalidStatus() {
        return [LeadStatus.INVALID, LeadStatus.LOST, LeadStatus.DUPLICATE].includes(this.status);
    }

    fill(payload) {
        if (!payload) {
            return;
        }

        Object.keys(payload).forEach((key) => {
            if (typeof this[key] !== 'undefined') {
                this[key] = payload[key];
            }
        });
    }

    fillCustomable(id, payload) {
        if (!payload) {
            return;
        }

        let leadVehicle = null;
        const value = typeof payload.value == 'string' ? [payload.value] : payload.value;
        let customField = this.custom_fields.find((customField) => customField.id == id);

        if (payload.vehicleId) {
            leadVehicle = this.vehicles.find((leadVehicle) => leadVehicle.id == payload.vehicleId);
            customField = leadVehicle?.custom_fields?.find((customField) => customField.id == id);
        }

        if (customField) {
            customField.pivot.value = value;
        } else {
            const newCustomField = {
                id,
                name: payload.label,
                field: payload.field,
                pivot: {
                    value,
                },
            };

            if (leadVehicle) {
                leadVehicle.custom_fields.push(newCustomField);
            } else {
                this.custom_fields.push(newCustomField);
            }
        }
    }

    fillVehicle(id, payload) {
        const vehicle = this.vehicles.find((v) => v.id == id);

        Object.keys(payload).forEach((key) => {
            if (typeof vehicle[key] !== 'undefined') {
                vehicle[key] = payload[key];
            }
        });
    }

    getVehicleCustomFieldsById(customFieldId, clientCardSection = '') {
        let vehicleType = '';
        let vehicles = this.vehicles;

        if (ClientCardSection.isVehicleExchangeField(clientCardSection)) {
            vehicleType = LeadVehicleType.EXCHANGE;
        } else if (ClientCardSection.isVehicleWantedField(clientCardSection)) {
            vehicleType = LeadVehicleType.WANTED;
        }

        if (vehicleType) {
            vehicles = vehicles.filter((v) => v.type === vehicleType);
        }

        if (vehicles.some((v) => v.sold)) {
            vehicles = vehicles.filter((v) => v.sold);
        }

        vehicles = vehicles
            .map((vehicle) => {
                return { custom_fields: vehicle.custom_fields, vehicle };
            })
            .flat();

        return vehicles.filter((obj) =>
            obj?.custom_fields?.filter((customField) => customField.id === customFieldId)
        );
    }

    getCustomableById(customFieldId) {
        return this.custom_fields.find((customField) => customField.id === customFieldId);
    }

    getCustomableVehicleById(customFieldId, clientCardSection) {
        const vehicles = this.getVehicleCustomFieldsById(customFieldId, clientCardSection);

        if (!vehicles.length) {
            return null;
        }

        return vehicles[0].custom_fields.find((customField) => customField.id == customFieldId);
    }

    disabledBySaleVerified(authUser, account = this.account) {
        return authUser?.id && account.verified_sale && this.hasVerifiedSoldVehicle();
    }

    allowCommunicationsWhenVerified(authUser) {
        return (
            authUser.id &&
            this.account.verified_sale &&
            this.account.allow_communications_when_verified_sale
        );
    }

    get userId() {
        return this.user_id || null;
    }

    set userId(userId) {
        this.user_id = userId;
    }

    get secondaryUserId() {
        return this.secondary_user_id || null;
    }

    set secondaryUserId(userId) {
        this.secondary_user_id = userId;
    }

    get secondUserId() {
        return this.second_user_id || null;
    }

    set secondUserId(userId) {
        this.second_user_id = userId;
    }

    get bdcUserId() {
        return this.bdc_user_id || null;
    }

    set bdcUserId(userId) {
        this.bdc_user_id = userId;
    }

    get secondBdcUserId() {
        return this.second_bdc_user_id || null;
    }

    set secondBdcUserId(userId) {
        this.second_bdc_user_id = userId;
    }

    get commercialId() {
        return this.commercial_id || null;
    }

    set commercialId(userId) {
        this.commercial_id = userId;
    }

    get deliveredById() {
        return this.delivered_by_id || null;
    }

    set deliveredById(userId) {
        this.delivered_by_id = userId;
    }

    get serviceAdvisorId() {
        return this.service_advisor_id || null;
    }

    set serviceAdvisorId(userId) {
        this.service_advisor_id = userId;
    }

    get serviceAgentId() {
        return this.service_agent_id || null;
    }

    set serviceAgentId(userId) {
        this.service_agent_id = userId;
    }

    get statusUpdatedById() {
        return this.status_updated_by_id || null;
    }

    set statusUpdatedById(userId) {
        this.status_updated_by_id = userId;
    }

    get takeOverDirectorId() {
        return this.take_over_director_id || null;
    }

    set takeOverDirectorId(userId) {
        this.take_over_director_id = userId;
    }

    get createdById() {
        return this.created_by || null;
    }

    set createdById(userId) {
        this.created_by = userId;
    }

    get updatedById() {
        return this.updated_by || null;
    }

    set updatedById(userId) {
        this.updated_by = userId;
    }

    get user() {
        if (!this.userId) {
            return new User();
        }

        return useUserStore().getUserById(this.userId) || new User();
    }

    get secondaryUser() {
        if (!this.secondaryUserId) {
            return new User();
        }

        return useUserStore().getUserById(this.secondaryUserId) || new User();
    }

    get secondUser() {
        if (!this.secondUserId) {
            return new User();
        }

        return useUserStore().getUserById(this.secondUserId) || new User();
    }

    get bdcUser() {
        if (!this.bdcUserId) {
            return new User();
        }

        return useUserStore().getUserById(this.bdcUserId) || new User();
    }

    get secondBdcUser() {
        if (!this.secondBdcUserId) {
            return new User();
        }

        return useUserStore().getUserById(this.secondBdcUserId) || new User();
    }

    get commercialUser() {
        if (!this.commercialId) {
            return new User();
        }

        return useUserStore().getUserById(this.commercialId) || new User();
    }

    get deliveredByUser() {
        if (!this.deliveredById) {
            return new User();
        }

        return useUserStore().getUserById(this.deliveredById) || new User();
    }

    get serviceAdvisorUser() {
        if (!this.serviceAdvisorId) {
            return new User();
        }

        return useUserStore().getUserById(this.serviceAdvisorId) || new User();
    }

    get serviceAgentUser() {
        if (!this.serviceAgentId) {
            return new User();
        }

        return useUserStore().getUserById(this.serviceAgentId) || new User();
    }

    get statusUpdatedByUser() {
        if (!this.statusUpdatedById) {
            return new User();
        }

        return useUserStore().getUserById(this.statusUpdatedById) || new User();
    }

    get takeOverDirectorUser() {
        if (!this.takeOverDirectorId) {
            return new User();
        }

        return useUserStore().getUserById(this.takeOverDirectorId) || new User();
    }

    get creatorUser() {
        const userData = {
            first_name: 'Activix',
            last_name: 'CRM',
        };

        if (this.createdById) {
            return useUserStore().getUserById(this.createdById) || new User(userData);
        }

        return new User(userData);
    }

    get updaterUser() {
        const userData = {
            first_name: 'Activix',
            last_name: 'CRM',
        };

        if (this.updatedById) {
            return useUserStore().getUserById(this.updatedById) || new User(userData);
        }

        return new User(userData);
    }
}
